import clsx from 'clsx';

interface ProgressBarProps {
  progress: number;
  label?: string;
  showPercentage?: boolean;
  size?: 'sm' | 'md' | 'lg';
  color?: 'brand' | 'green' | 'yellow' | 'red';
  className?: string;
}

export function ProgressBar({
  progress,
  label,
  showPercentage = true,
  size = 'md',
  color = 'brand',
  className,
}: ProgressBarProps) {
  const clampedProgress = Math.min(100, Math.max(0, progress));

  const sizeClasses = {
    sm: 'h-1.5',
    md: 'h-2.5',
    lg: 'h-4',
  };

  const colorClasses = {
    brand: 'bg-brand-600',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
  };

  return (
    <div className={className}>
      {(label || showPercentage) && (
        <div className="flex justify-between items-center mb-1">
          {label && <span className="text-sm font-medium text-surface-700">{label}</span>}
          {showPercentage && (
            <span className="text-sm font-medium text-surface-600">
              {Math.round(clampedProgress)}%
            </span>
          )}
        </div>
      )}
      <div className={clsx('bg-surface-200 rounded-full overflow-hidden', sizeClasses[size])}>
        <div
          className={clsx(
            'h-full transition-all duration-500 ease-out rounded-full',
            colorClasses[color]
          )}
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
    </div>
  );
}

interface CrawlProgressProps {
  totalUrls: number;
  crawledUrls: number;
  analyzedUrls: number;
  failedUrls: number;
  status: string;
}

export function CrawlProgress({
  totalUrls,
  crawledUrls,
  analyzedUrls,
  failedUrls,
  status,
}: CrawlProgressProps) {
  const progress = totalUrls > 0 ? (analyzedUrls / totalUrls) * 100 : 0;

  return (
    <div className="space-y-4">
      <ProgressBar
        progress={progress}
        size="lg"
        color={status === 'completed' ? 'green' : status === 'failed' ? 'red' : 'brand'}
      />

      <div className="grid grid-cols-4 gap-4 text-center">
        <div>
          <p className="text-2xl font-bold text-surface-900">{totalUrls}</p>
          <p className="text-xs text-surface-500">Total URLs</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-blue-600">{crawledUrls}</p>
          <p className="text-xs text-surface-500">Crawled</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-green-600">{analyzedUrls}</p>
          <p className="text-xs text-surface-500">Analyzed</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-red-600">{failedUrls}</p>
          <p className="text-xs text-surface-500">Failed</p>
        </div>
      </div>

      <div className="text-center">
        <span className={clsx(
          'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
          status === 'running' && 'bg-blue-100 text-blue-800',
          status === 'completed' && 'bg-green-100 text-green-800',
          status === 'failed' && 'bg-red-100 text-red-800',
          status === 'paused' && 'bg-yellow-100 text-yellow-800',
          status === 'pending' && 'bg-surface-100 text-surface-800',
        )}>
          {status === 'running' && (
            <span className="w-2 h-2 bg-blue-600 rounded-full mr-2 animate-pulse" />
          )}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>
    </div>
  );
}
